// src/PricingPage.tsx
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaCheck, FaBriefcase, FaFileAlt, FaCrown, FaRocket, FaCoins, FaStar, FaUsers, FaChartLine, FaChevronDown, FaBolt, FaInfinity, FaCalculator, FaSync } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { enhancedPlans, Subscriptions } from '../features/payments/enhanced-pricing-plans.type';
import SEO from '../features/seo/seo.feature';
import { analytics } from '../features/firebase/firebase-config';
import PricingCalculator from '../components/PricingCalculator';
import PlanComparisonTable from '../components/PlanComparisonTable';

const PricingPage: React.FC = () => {
  analytics.logEvent('page_view', {
    page_title: 'pricing',
  });

  // Interactive state management
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [hoveredPlan, setHoveredPlan] = useState<string | null>(null);
  const [showCalculator, setShowCalculator] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);
  const [pricingMode, setPricingMode] = useState<'subscription' | 'pay-as-you-go'>('subscription');

  // Pay-as-you-go pricing structure
  const payAsYouGoPricing = {
    interviewSession: { credits: 5, price: 0.50, description: 'AI-powered interview practice session' },
    jobAnalysis: { credits: 3, price: 0.30, description: 'Smart job description analysis' },
    cvGeneration: { credits: 8, price: 0.80, description: 'ATS-optimized CV generation' },
    companyResearch: { credits: 2, price: 0.20, description: 'Company insights and research' },
    skillsGapAnalysis: { credits: 4, price: 0.40, description: 'Personalized skills assessment' }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        damping: 20,
        stiffness: 100
      }
    },
    hover: {
      scale: 1.05,
      y: -10,
      transition: {
        type: "spring",
        damping: 15,
        stiffness: 300
      }
    }
  };

  const featureVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        type: "spring",
        damping: 20
      }
    }
  };

  // FAQ data
  const faqData = [
    {
      question: "What's the difference between subscription and pay-as-you-go?",
      answer: "Subscriptions give you unlimited access to features within your plan limits, while pay-as-you-go lets you purchase credits to use features only when you need them. Perfect for occasional users or those who want maximum flexibility."
    },
    {
      question: "Do credits expire?",
      answer: "Credits never expire! You can use them whenever you need them, making it perfect for irregular usage patterns or seasonal job searching."
    },
    {
      question: "Can I switch between subscription and pay-as-you-go?",
      answer: "Absolutely! You can switch between pricing models at any time. If you have unused credits when switching to a subscription, they'll be credited to your account."
    },
    {
      question: "Which option is better for me?",
      answer: "If you use PrepBestie regularly (3+ times per week), a subscription is more cost-effective. If you use it occasionally or want to try specific features, pay-as-you-go gives you maximum flexibility."
    }
  ];

  // Testimonials data
  const testimonials = [
    {
      name: "Sarah Johnson",
      role: "Software Engineer",
      company: "TechCorp",
      content: "The pay-as-you-go option was perfect for my job search! I only paid for what I used and saved money compared to a monthly subscription.",
      avatar: "SJ",
      rating: 5,
      pricingType: "Pay-as-you-go"
    },
    {
      name: "Michael Chen",
      role: "Product Manager",
      company: "StartupXYZ",
      content: "The Pro subscription gives me everything I need for regular practice. The unlimited sessions are worth every penny!",
      avatar: "MC",
      rating: 5,
      pricingType: "Pro Subscription"
    },
    {
      name: "Emily Rodriguez",
      role: "Marketing Director",
      company: "GrowthCo",
      content: "I love the flexibility! I can use credits for intensive interview prep before big interviews, then switch to subscription for regular practice.",
      avatar: "ER",
      rating: 5,
      pricingType: "Hybrid User"
    }
  ];

  // Define structured data for better SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "PriceSpecification",
    "name": "PrepBestie Pricing Plans",
    "description": "Flexible pricing options: subscription plans or pay-as-you-go credits for AI-powered interview preparation",
    "offers": [
      ...enhancedPlans.map(plan => ({
        "@type": "Offer",
        "name": plan.name,
        "price": plan.amount.toString(),
        "priceCurrency": "USD",
        "description": `${plan.jobAnalysisSessions} job analyses, ${plan.cvGenerations} CV generations`,
        "url": `https://prepbestie.com/create-account`
      })),
      {
        "@type": "Offer",
        "name": "Pay-as-you-go Credits",
        "price": "0.10",
        "priceCurrency": "USD",
        "description": "Flexible credit-based pricing for occasional users",
        "url": `https://prepbestie.com/create-account`
      }
    ],
    "publisher": {
      "@type": "Organization",
      "name": "PrepBestie",
      "logo": {
        "@type": "ImageObject",
        "url": "https://prepbestie.com/prepbestie.png"
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 text-white overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-pink-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse animation-delay-4000"></div>
      </div>

      <SEO
        title='Pricing | PrepBestie'
        description='Choose your perfect pricing: flexible pay-as-you-go credits or unlimited subscription plans. AI-powered interview preparation that fits your needs.'
        name='PrepBestie'
        type='website'
        structuredData={structuredData}
        keywords={[
          'PrepBestie pricing',
          'pay as you go interview practice',
          'interview preparation credits',
          'AI interview practice pricing',
          'flexible interview coaching',
          'job search tools pricing'
        ]}
      />

      <div className="relative z-10">
        {/* Hero Section */}
        <motion.div 
          className="text-center py-20 px-4"
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <motion.h1 
            className="text-6xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-white via-purple-200 to-pink-200 bg-clip-text text-transparent"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Choose Your Perfect Pricing
          </motion.h1>
          <motion.p 
            className="text-xl md:text-2xl text-purple-100 mb-8 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            Flexible options: Pay-as-you-go credits or unlimited subscription plans
          </motion.p>
          
          {/* Pricing Mode Toggle */}
          <motion.div 
            className="flex items-center justify-center mb-12"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <div className="bg-white/10 backdrop-blur-sm rounded-full p-1 flex">
              <button
                onClick={() => setPricingMode('subscription')}
                className={`px-8 py-4 rounded-full transition-all duration-300 flex items-center ${
                  pricingMode === 'subscription' 
                    ? 'bg-white text-purple-900 font-semibold shadow-lg' 
                    : 'text-white hover:bg-white/20'
                }`}
              >
                <FaInfinity className="mr-2" />
                Subscriptions
              </button>
              <button
                onClick={() => setPricingMode('pay-as-you-go')}
                className={`px-8 py-4 rounded-full transition-all duration-300 flex items-center ${
                  pricingMode === 'pay-as-you-go' 
                    ? 'bg-white text-purple-900 font-semibold shadow-lg' 
                    : 'text-white hover:bg-white/20'
                }`}
              >
                <FaCoins className="mr-2" />
                Pay-as-you-go
              </button>
            </div>
          </motion.div>

          {/* Interactive Tools */}
          <motion.div 
            className="flex flex-col items-center justify-center mb-12"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            <div className="flex flex-col sm:flex-row gap-4">
              {pricingMode === 'pay-as-you-go' && (
                <motion.button
                  onClick={() => setShowCalculator(true)}
                  className="bg-gradient-to-r from-pink-500 to-purple-600 text-white px-8 py-4 rounded-full font-bold text-lg hover:shadow-lg transition-all duration-300 flex items-center justify-center"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FaCalculator className="mr-2" />
                  Usage Calculator
                </motion.button>
              )}
              
              <motion.button
                onClick={() => setShowComparison(true)}
                className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-8 py-4 rounded-full font-bold text-lg hover:shadow-lg transition-all duration-300 flex items-center justify-center"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaChartLine className="mr-2" />
                Compare Options
              </motion.button>
            </div>
          </motion.div>
        </motion.div>

        {/* Dynamic Pricing Content */}
        <AnimatePresence mode="wait">
          {pricingMode === 'subscription' ? (
            <motion.div
              key="subscription"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -50 }}
              transition={{ duration: 0.5 }}
            >
              {/* Subscription Plans */}
              <motion.div 
                className="max-w-7xl mx-auto px-4 pb-20"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                <div className="grid gap-8 grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
                  {enhancedPlans.map((plan, index) => (
                    <motion.div
                      key={index}
                      variants={cardVariants}
                      whileHover="hover"
                      className={`relative group cursor-pointer ${
                        plan.name === Subscriptions.pro ? 'lg:scale-110 z-10' : ''
                      }`}
                      onMouseEnter={() => setHoveredPlan(plan.name)}
                      onMouseLeave={() => setHoveredPlan(null)}
                      onClick={() => setSelectedPlan(plan.name)}
                    >
                      {/* Popular Badge */}
                      {plan.name === Subscriptions.pro && (
                        <motion.div 
                          className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-20"
                          initial={{ opacity: 0, y: -20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.5 + index * 0.1 }}
                        >
                          <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-gray-900 px-6 py-2 rounded-full font-bold text-sm shadow-lg flex items-center">
                            <FaCrown className="mr-2" />
                            MOST POPULAR
                          </div>
                        </motion.div>
                      )}

                      <motion.div
                        className={`relative overflow-hidden rounded-3xl shadow-2xl transition-all duration-500 ${
                          plan.name === Subscriptions.pro 
                            ? 'bg-gradient-to-br from-purple-600 via-pink-600 to-red-500 border-2 border-yellow-400' 
                            : 'bg-white/10 backdrop-blur-sm border border-white/20'
                        } ${
                          selectedPlan === plan.name ? 'ring-4 ring-white/50' : ''
                        } ${
                          hoveredPlan === plan.name ? 'shadow-3xl' : ''
                        }`}
                        whileHover={{ 
                          scale: plan.name === Subscriptions.pro ? 1.05 : 1.02,
                          rotateY: 5,
                          transition: { duration: 0.3 }
                        }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <div className={`p-8 relative z-10 ${plan.name === Subscriptions.pro ? 'pt-16' : ''}`}>
                          {/* Plan Icon */}
                          <motion.div 
                            className="text-center mb-6"
                            whileHover={{ rotate: 360 }}
                            transition={{ duration: 0.6 }}
                          >
                            {plan.name === Subscriptions.free && <FaRocket className="text-4xl mx-auto mb-4 text-blue-400" />}
                            {plan.name === Subscriptions.basic && <FaBriefcase className="text-4xl mx-auto mb-4 text-green-400" />}
                            {plan.name === Subscriptions.pro && <FaCrown className="text-4xl mx-auto mb-4 text-yellow-400" />}
                            {plan.name === Subscriptions.enterprise && <FaUsers className="text-4xl mx-auto mb-4 text-purple-400" />}
                          </motion.div>

                          {/* Plan Name */}
                          <h3 className="text-2xl font-bold text-center mb-4 text-white">
                            {plan.name}
                          </h3>

                          {/* Price */}
                          <motion.div 
                            className="text-center mb-6"
                            whileHover={{ scale: 1.05 }}
                          >
                            <div className="text-5xl font-bold text-white mb-2">
                              {plan.amount === 0 ? 'Free' : `$${plan.amount}`}
                            </div>
                            {plan.amount > 0 && (
                              <div className="text-lg text-purple-200">
                                /month
                              </div>
                            )}
                          </motion.div>

                          {/* Description */}
                          <p className="text-center text-purple-100 mb-8 text-sm">
                            {plan.description}
                          </p>

                          {/* Features */}
                          <motion.div 
                            className="space-y-4 mb-8"
                            variants={featureVariants}
                          >
                            {/* Interview Features */}
                            <div>
                              <h4 className="font-semibold text-white mb-3 flex items-center">
                                <FaBriefcase className="mr-2 text-blue-400" />
                                Interview Practice
                              </h4>
                              <div className="space-y-2">
                                <div className="flex items-center text-sm text-purple-100">
                                  <FaCheck className="mr-2 text-green-400 flex-shrink-0" />
                                  {plan.sessions === -1 ? 'Unlimited' : `${plan.sessions}`} sessions/month
                                </div>
                                <div className="flex items-center text-sm text-purple-100">
                                  <FaCheck className="mr-2 text-green-400 flex-shrink-0" />
                                  {plan.recordingLength}min recordings
                                </div>
                                {plan.emailReminder && (
                                  <div className="flex items-center text-sm text-purple-100">
                                    <FaCheck className="mr-2 text-green-400 flex-shrink-0" />
                                    Email reminders
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Job Analysis Features */}
                            <div>
                              <h4 className="font-semibold text-white mb-3 flex items-center">
                                <FaFileAlt className="mr-2 text-green-400" />
                                Job Analysis
                              </h4>
                              <div className="space-y-2">
                                <div className="flex items-center text-sm text-purple-100">
                                  <FaCheck className="mr-2 text-green-400 flex-shrink-0" />
                                  {plan.jobAnalysisSessions === -1 ? 'Unlimited' : `${plan.jobAnalysisSessions}`} analyses/month
                                </div>
                                <div className="flex items-center text-sm text-purple-100">
                                  <FaCheck className="mr-2 text-green-400 flex-shrink-0" />
                                  {plan.cvGenerations === -1 ? 'Unlimited' : `${plan.cvGenerations}`} CV generations
                                </div>
                                {plan.companyResearch && (
                                  <div className="flex items-center text-sm text-purple-100">
                                    <FaCheck className="mr-2 text-green-400 flex-shrink-0" />
                                    Company research
                                  </div>
                                )}
                              </div>
                            </div>
                          </motion.div>

                          {/* CTA Button */}
                          <motion.div
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <Link
                              to="/create-account"
                              className={`block w-full py-4 px-6 rounded-xl font-semibold text-center transition-all duration-300 ${
                                plan.name === Subscriptions.pro
                                  ? 'bg-white text-purple-900 hover:bg-gray-100 shadow-lg'
                                  : 'bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm'
                              }`}
                            >
                              {plan.amount === 0 ? 'Get Started Free' : 'Choose Plan'}
                            </Link>
                          </motion.div>
                        </div>
                      </motion.div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          ) : (
            <motion.div
              key="pay-as-you-go"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -50 }}
              transition={{ duration: 0.5 }}
            >
              {/* Pay-as-you-go Pricing */}
              <motion.div 
                className="max-w-7xl mx-auto px-4 pb-20"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                {/* Feature Pricing */}
                <div className="mb-16">
                  <h2 className="text-4xl font-bold text-center mb-8 text-white">Pay Only for What You Use</h2>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {Object.entries(payAsYouGoPricing).map(([key, feature], index) => (
                      <motion.div
                        key={key}
                        className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 hover:bg-white/20 transition-all duration-300"
                        whileHover={{ scale: 1.05, y: -5 }}
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <div className="flex items-center justify-between mb-6">
                          <div className="flex items-center">
                            <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mr-4">
                              <FaCoins className="text-white text-xl" />
                            </div>
                            <div>
                              <h3 className="font-bold text-white text-lg capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</h3>
                              <p className="text-sm text-purple-200">{feature.description}</p>
                            </div>
                          </div>
                        </div>
                        
                        {/* Cost and Value Display */}
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <div className="text-3xl font-bold text-white">
                              {feature.credits} credits
                            </div>
                            <div className="text-2xl text-purple-200 font-semibold">
                              ${feature.price}
                            </div>
                          </div>
                          
                          {/* Feature Value Breakdown */}
                          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                            <div className="text-center">
                              <div className="text-sm text-purple-200 mb-2">What you get:</div>
                              <div className="text-2xl font-bold text-white mb-1">
                                1 {key.replace(/([A-Z])/g, ' $1').trim().toLowerCase()}
                              </div>
                              <div className="text-sm text-purple-300">
                                per {feature.credits} credits
                              </div>
                            </div>
                          </div>
                          
                          {/* Additional Value Examples */}
                          <div className="text-center">
                            <div className="text-sm text-purple-200 mb-2">With 100 credits you can get:</div>
                            <div className="text-lg font-semibold text-white">
                              {Math.floor(100 / feature.credits)} {key.replace(/([A-Z])/g, ' $1').trim().toLowerCase()}s
                            </div>
                            <div className="text-sm text-purple-300">
                              (${(Math.floor(100 / feature.credits) * feature.price).toFixed(2)} value)
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Usage Calculator Preview */}
                <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8">
                  <h2 className="text-3xl font-bold text-center mb-8 text-white">Quick Usage Preview</h2>
                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <h3 className="text-xl font-semibold mb-4 text-white">Feature Costs</h3>
                      <div className="space-y-4">
                        {Object.entries(payAsYouGoPricing).map(([key, feature]) => (
                          <div key={key} className="flex items-center justify-between bg-white/10 rounded-lg p-4">
                            <div>
                              <span className="text-white font-medium">{feature.description}</span>
                              <div className="text-sm text-purple-200">{feature.credits} credits each</div>
                            </div>
                            <div className="text-right">
                              <div className="text-lg font-bold text-white">${feature.price}</div>
                              <div className="text-sm text-purple-200">per use</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl p-6">
                      <h3 className="text-xl font-semibold mb-4 text-white">Need Help Calculating?</h3>
                      <p className="text-purple-100 mb-6">Use our detailed calculator to estimate your monthly usage and find the perfect credit package.</p>
                      <motion.button
                        onClick={() => setShowCalculator(true)}
                        className="w-full bg-white text-purple-900 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-all duration-300"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        Open Usage Calculator
                      </motion.button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Interactive Features Section */}
        <motion.div 
          className="max-w-6xl mx-auto px-4 py-20"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-white">Why Choose PrepBestie?</h2>
            <p className="text-xl text-purple-200">Flexible pricing that adapts to your needs</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <FaBolt className="text-4xl text-blue-400" />,
                title: "Pay-as-you-go Flexibility",
                description: "Use only what you need, when you need it. Perfect for occasional users or specific interview prep."
              },
              {
                icon: <FaInfinity className="text-4xl text-green-400" />,
                title: "Unlimited Subscriptions",
                description: "Unlimited access to all features for regular users. Best value for consistent practice."
              },
              {
                icon: <FaSync className="text-4xl text-purple-400" />,
                title: "Switch Anytime",
                description: "Change between pricing models anytime. Your credits are always safe and never expire."
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-center hover:bg-white/20 transition-all duration-300"
                whileHover={{ scale: 1.05, y: -5 }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                viewport={{ once: true }}
              >
                <motion.div
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.6 }}
                  className="mb-4"
                >
                  {feature.icon}
                </motion.div>
                <h3 className="text-xl font-bold text-white mb-4">{feature.title}</h3>
                <p className="text-purple-200">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Interactive Testimonials */}
        <motion.div 
          className="max-w-6xl mx-auto px-4 py-20"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-white">What Our Users Say</h2>
            <p className="text-xl text-purple-200">Real success stories from our community</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 hover:bg-white/20 transition-all duration-300"
                whileHover={{ scale: 1.02, y: -5 }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                viewport={{ once: true }}
              >
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold mr-4">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <h4 className="font-semibold text-white">{testimonial.name}</h4>
                    <p className="text-purple-200 text-sm">{testimonial.role} at {testimonial.company}</p>
                    <p className="text-yellow-400 text-xs font-semibold">{testimonial.pricingType}</p>
                  </div>
                </div>
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <FaStar key={i} className="text-yellow-400" />
                  ))}
                </div>
                <p className="text-purple-100 italic">"{testimonial.content}"</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Interactive FAQ Section */}
        <motion.div 
          className="max-w-4xl mx-auto px-4 py-20"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-white">Frequently Asked Questions</h2>
            <p className="text-xl text-purple-200">Everything you need to know about our pricing</p>
          </div>

          <div className="space-y-4">
            {faqData.map((faq, index) => (
              <motion.div
                key={index}
                className="bg-white/10 backdrop-blur-sm rounded-2xl overflow-hidden"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <button
                  onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)}
                  className="w-full p-6 text-left flex items-center justify-between hover:bg-white/20 transition-colors duration-300"
                >
                  <h3 className="text-lg font-semibold text-white">{faq.question}</h3>
                  <motion.div
                    animate={{ rotate: expandedFAQ === index ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <FaChevronDown className="text-purple-300" />
                  </motion.div>
                </button>
                <AnimatePresence>
                  {expandedFAQ === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6 text-purple-200">
                        {faq.answer}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Call to Action */}
        <motion.div 
          className="text-center py-20 px-4"
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="max-w-4xl mx-auto">
            <h2 className="text-5xl font-bold mb-6 text-white">Ready to Ace Your Next Interview?</h2>
            <p className="text-xl text-purple-200 mb-8">Choose your perfect pricing option and start your journey today</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to="/create-account"
                  className="inline-block bg-gradient-to-r from-purple-600 to-pink-600 text-white px-12 py-4 rounded-full text-xl font-bold shadow-2xl hover:shadow-purple-500/25 transition-all duration-300"
                >
                  Start with Free Plan
                </Link>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to="/create-account"
                  className="inline-block bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-12 py-4 rounded-full text-xl font-bold shadow-2xl hover:shadow-blue-500/25 transition-all duration-300"
                >
                  Buy Credits Now
                </Link>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Pricing Calculator Modal */}
      <PricingCalculator 
        isOpen={showCalculator} 
        onClose={() => setShowCalculator(false)} 
      />

      {/* Plan Comparison Modal */}
      <PlanComparisonTable 
        isOpen={showComparison} 
        onClose={() => setShowComparison(false)} 
      />
    </div>
  );
};

export default PricingPage;